/**
 * @typedef {Object} FloatingBannerData
 * @property {string} bannerId - 배너 고유 ID
 * @property {number} areaType - 배너 영역 타입
 * @property {number} positionType - 배너 위치 타입 (0: append, 1: before, 2: after, 3: prepend)
 * @property {number} order - 배너 순서
 * @property {string} moveTarget - 배너가 삽입될 타겟 CSS 선택자
 * @property {string} contents - 배너 HTML 콘텐츠
 */

/**
 * 플로팅 배너 관리 클래스
 * @class FloatingBannerManager
 * 렌더링된 플로팅 배너를 타겟 위치로 이동하고 CustomEvent를 발행하는 관리자.
 * 인스턴스 상태가 없는 순수 유틸이므로 전체 static으로 구성한다.
 * (생성/싱글톤 관리 불필요 — 상태가 생기면 그때 인스턴스화를 검토)
 */
class FloatingBannerManager {
  /**
   * 배너 위치 타입 상수
   * @static
   * @readonly
   * @enum {number}
   */
  static POSITION_TYPE = {
    APPEND: 0,
    BEFORE: 1,
    AFTER: 2,
    PREPEND: 3
  };

  /**
   * 커스텀 이벤트명
   * @static
   * @readonly
   */
  static EVENT_NAME = 'event.floatingBannerInserted';

  /**
   * 기본 fallback 선택자
   * @static
   * @readonly
   */
  static DEFAULT_TARGET = 'body';

  /**
   * 렌더링된 배너들을 타겟 위치로 이동
   * @static
   * @param {string} [bannerIds] - 처리할 배너 ID 목록 (쉼표 구분). 미전달 시 전체 처리
   * @returns {void}
   */
  static init(bannerIds) {
    const $banners = FloatingBannerManager.selectTargetBanners(bannerIds);
    for (const $banner of $banners) {
      try {
        FloatingBannerManager.moveBanner($banner);
      } catch (error) {
        console.error('[FloatingBanner] 배너 이동 실패:', error);
      }
    }
  }

  /**
   * 처리 대상 배너 선택
   * ID 스코핑은 컴포넌트 간 “리스너 등록 → dispatch” 순서 보장,
   * :not([data-initialized])는 배너당 1회 처리 보장(멱등성). 역할이 달라 둘 다 필요하다.
   * @static
   * @param {string} [bannerIds] - 쉼표 구분 배너 ID 목록
   * @returns {HTMLElement[]|NodeList} ID 목록 순서를 따르는 배너 목록
   * @private
   */
  static selectTargetBanners(bannerIds) {
    if (!bannerIds || 'string' !== typeof bannerIds || !bannerIds.trim()) {
      return document.querySelectorAll('.floatingBanner[data-move]:not([data-initialized])');
    }

    const $banners = [];

    for (const rawId of bannerIds.split(',')) {
      const id = rawId.trim();

      // 셀렉터 안전성: 영숫자/하이픈/언더스코어만 허용
      if (!id || false === /^[\w-]+$/.test(id)) {
        continue;
      }

      const $matched = document.querySelectorAll(`.floatingBanner[data-move][data-id="${id}"]:not([data-initialized])`);
      $banners.push(...$matched);
    }

    return $banners;
  }

  /**
   * 개별 배너를 타겟 위치로 이동
   * @static
   * @param {HTMLElement} $banner - 배너 요소
   * @returns {void}
   * @private
   */
  static moveBanner($banner) {
    let moveTarget = $banner.dataset.move;
    if (!moveTarget || !moveTarget.trim()) {
      moveTarget = FloatingBannerManager.DEFAULT_TARGET;
    }

    const $target = document.querySelector(moveTarget);
    if (!$target) {
      return;
    }

    const positionType = parseInt($banner.dataset.position, 10) || 0;

    // 타겟 위치로 이동
    FloatingBannerManager.insertBanner($target, $banner, positionType);

    // 표시
    $banner.style.display = '';

    // 처리 완료 표시 (멱등성 가드: 재호출 시 중복 이동/dispatch 방지)
    $banner.dataset.initialized = 'true';

    // 삽입 완료 이벤트 발행
    FloatingBannerManager.dispatchInsertedEvent($banner);
  }

  /**
   * 배너를 지정된 위치에 삽입
   * @static
   * @param {HTMLElement} $target - 타겟 요소
   * @param {HTMLElement} $banner - 배너 요소
   * @param {number} positionType - 위치 타입
   * @returns {void}
   * @private
   */
  static insertBanner($target, $banner, positionType) {
    switch (positionType) {
      case FloatingBannerManager.POSITION_TYPE.BEFORE:
        $target.parentNode.insertBefore($banner, $target);
        break;
      case FloatingBannerManager.POSITION_TYPE.AFTER:
        $target.parentNode.insertBefore($banner, $target.nextSibling);
        break;
      case FloatingBannerManager.POSITION_TYPE.PREPEND:
        $target.insertBefore($banner, $target.firstChild);
        break;
      default:
        $target.appendChild($banner);
        break;
    }
  }

  /**
   * 배너 삽입 완료 이벤트 발행
   * @static
   * @param {HTMLElement} $banner - 삽입된 배너 요소
   * @returns {void}
   * @private
   */
  static dispatchInsertedEvent($banner) {
    const bannerId = $banner.dataset.id;
    if (!bannerId) {
      return;
    }

    document.dispatchEvent(
      new CustomEvent(FloatingBannerManager.EVENT_NAME, {
        detail: {
          bannerId: bannerId,
          moveTarget: $banner.dataset.move,
          areaType: parseInt($banner.dataset.area, 10) || 0,
          order: parseInt($banner.dataset.orderby, 10) || 0,
          element: $banner
        }
      })
    );
  }
}

// 전역 네임스페이스에 등록
window._pa = window._pa || {};
window._pa.FloatingBannerManager = FloatingBannerManager;
